import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, GraduationCap, Calendar, Award, Flame, BookOpen, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';

const TIER_LABELS = { school: 'School', intermediate: 'Intermediate', btech: 'B.Tech' };

function StatBlock({ label, value, sub, icon: Icon, color = '#2563eb' }) {
  return (
    <div style={{ padding: '14px 16px', background: '#f8fafc', borderRadius: 14, border: '1px solid rgba(0,0,0,.05)', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</span>
        {Icon && <Icon size={16} color={color} />}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy-dark)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#64748b' }}>{sub}</div>}
    </div>
  );
}

export default function ProgressReportView({ heading = 'Student Progress Report', subheading = 'Learning analytics and progress overview' }) {
  const { user, profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/'); return; }
    api.getMyDashboard().then(setDashboardData).catch(() => setDashboardData(null)).finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  if (authLoading) return <div style={{ padding: '6rem 2rem', textAlign: 'center', color: '#64748b' }}>Loading report...</div>;
  if (!user) return null;

  const today = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  const userData = dashboardData?.user || user;
  const awards = dashboardData?.awards || [];
  const enrollments = dashboardData?.enrollments || [];
  const applications = dashboardData?.applications || [];

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', padding: '2rem 1rem' }} className="fade-in-up">
      <div className="parent-card-print-hide" style={{ maxWidth: 760, margin: '0 auto 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button
          onClick={() => navigate('/dashboard')}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#475569', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
        >
          <ArrowLeft size={16} /> Back to Dashboard
        </button>
        <button
          onClick={() => window.print()}
          className="btn-lift"
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#0f172a', color: '#fff', border: 'none', borderRadius: 10, padding: '8px 16px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
        >
          <Printer size={14} /> Print / Save as PDF
        </button>
      </div>

      <div style={{ maxWidth: 760, margin: '0 auto', background: '#fff', borderRadius: 20, padding: '2.5rem', boxShadow: '0 4px 20px rgba(0,0,0,.05)', border: '1px solid rgba(0,0,0,.05)' }}>

        {/* Letterhead */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid var(--navy-dark)', paddingBottom: '1.25rem', marginBottom: '1.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/Logo.jpeg" alt="Ekalavya" style={{ width: 40, height: 40, borderRadius: 8 }} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy-dark)' }}>Ekalavya</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{subheading}</div>
            </div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 5 }}>
            <Calendar size={12} />{today}
          </div>
        </div>

        <div style={{ fontSize: 12, fontWeight: 700, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '0.5rem' }}>{heading}</div>

        {/* Student info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: '2rem' }}>
          <img
            src={user.avatarUrl || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.id}`}
            alt=""
            style={{ width: 64, height: 64, borderRadius: '50%', border: '1px solid #e2e8f0' }}
          />
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy-dark)' }}>{profile?.full_name || 'Scholar'}</div>
            <div style={{ display: 'flex', gap: 12, marginTop: 4, fontSize: 12, color: '#64748b' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><GraduationCap size={13} />{TIER_LABELS[profile?.studentTier] || 'Student'}</span>
              {profile?.institution && <span>{profile.institution}</span>}
            </div>
          </div>
        </div>

        {loading ? (
          <p style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem 0' }}>Loading report...</p>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: '2rem' }}>
              <StatBlock label="Learning Score" value={userData?.score || 0} sub="Total points earned" icon={Award} color="#8b5cf6" />
              <StatBlock label="Study Streak" value={`${userData?.streak || 0} days`} sub="Consecutive days active" icon={Flame} color="#f97316" />
              <StatBlock label="Mentoring" value={enrollments.length} sub="Sessions attended" icon={Users} color="#2563eb" />
              <StatBlock label="Scholarships" value={applications.length} sub="Applications submitted" icon={BookOpen} color="#10b981" />
            </div>

            <h3 style={{ fontSize: 13, fontWeight: 800, color: 'var(--navy-dark)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12, borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>
              Achievements & Badges
            </h3>

            {awards.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: '1.5rem' }}>
                {awards.map((award) => (
                  <div key={award._id || award.id} style={{ padding: '12px 14px', borderRadius: 12, background: 'rgba(147,51,234,0.04)', border: '1px solid rgba(147,51,234,0.12)', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #9333ea, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 18, flexShrink: 0 }}>
                      🏆
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-dark)' }}>{award.label || award.type}</div>
                      <div style={{ fontSize: 10, color: '#64748b' }}>Earned {new Date(award.awardedAt || Date.now()).toLocaleDateString('en-IN')}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: 12, color: '#94a3b8', padding: '1rem 0' }}>No badges unlocked yet. Keep studying to earn achievements!</p>
            )}

            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: '1.5rem', lineHeight: 1.6 }}>
              This progress report reflects genuine academic engagement, peer learning, mentoring sessions, and study consistency on the Ekalavya platform.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
